import { EditorSelection, Facet } from "@codemirror/state"
import type { EditorView } from "@codemirror/view"
import { formatWithCursor } from "prettier"
import babelPlugin from "prettier/plugins/babel"
import estreePlugin from "prettier/plugins/estree"
import htmlPlugin from "prettier/plugins/html"

export const formatCallbackFacet = Facet.define<
  (formattedCode: string) => void,
  (formattedCode: string) => void
>({
  combine: (callbacks) => callbacks[0] ?? (() => {}),
})

/**
 * 格式化 TypeScript/JavaScript 代码（用于 code-editor）
 */
export function formatDocument(view: EditorView) {
  const onFormat = view.state.facet(formatCallbackFacet)
  const code = view.state.doc.toString()

  async function format() {
    try {
      const plugins = [babelPlugin, estreePlugin]

      const result = await formatWithCursor(code, {
        cursorOffset: view.state.selection.main.anchor,
        parser: "babel",
        plugins,
      })

      view.dispatch({
        changes: {
          from: 0,
          to: view.state.doc.length,
          insert: result.formatted,
        },
        selection: EditorSelection.single(result.cursorOffset),
      })

      onFormat(result.formatted)
    } catch (error) {
      console.error("Format error:", error)
    }
  }

  void format()
  return true
}

/**
 * 格式化 HTML 代码（用于 js-editor）
 * HTML 格式化会同时格式化 script 标签内的 JavaScript
 */
export function formatHtmlDocument(view: EditorView) {
  const onFormat = view.state.facet(formatCallbackFacet)
  const code = view.state.doc.toString()

  async function format() {
    try {
      // HTML 格式化需要 html 插件和 babel/estree 插件（用于格式化 script 标签内的 JS）
      const plugins = [htmlPlugin, babelPlugin, estreePlugin]

      const result = await formatWithCursor(code, {
        cursorOffset: view.state.selection.main.anchor,
        parser: "html",
        plugins,
      })

      view.dispatch({
        changes: {
          from: 0,
          to: view.state.doc.length,
          insert: result.formatted,
        },
        selection: EditorSelection.single(result.cursorOffset),
      })

      onFormat(result.formatted)
    } catch (error) {
      console.error("Format error:", error)
    }
  }

  void format()
  return true
}

export function createFormatExtension(onFormat: (formattedCode: string) => void) {
  return [formatCallbackFacet.of(onFormat)]
}
